import React from 'react'
import PropTypes from 'prop-types'
import { setPageTitle } from 'utils/misc'
import { browserHistory } from 'utils/navigation'
import { Button } from 'components/UIElements'
import AdvancedOptionsHOC from 'utils/hoc/AdvancedOptionsHOC'
import { buttonContentOnSendState } from 'utils/helpers'
import '../stylesheets/announcement.scss'
import DeliverySection from './DeliverySection'
import MessageSection from './MessageSection'
import RecipientSection from './RecipientSection'
import ReadStatus from './ReadStatus'

const isSmsCommunicationRoute = ({ is_sms, location, params } = {}) => {
  if (is_sms) return true
  const pathname = location?.pathname || ''
  return pathname.includes('/createsms') || pathname.includes('/sms/') || Boolean(params?.smsID)
}

class AnnouncementPage extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      isSMS: isSmsCommunicationRoute(this.props),
    }
  }

  componentDidMount() {
    const { studyID, setNavBarProps, setCommunicationType, params } = this.props
    const { studyVersionID } = params
    const { isSMS } = this.state
    setCommunicationType(isSMS ? 'sms' : 'announcements')
    setNavBarProps({
      close: `/studies/${studyID}/${studyVersionID}/communication`,
      closeText: 'Close',
    })
  }

  componentWillUnmount() {
    const { setNavBarProps, resetAnnouncement } = this.props
    resetAnnouncement()
    setNavBarProps(null)
  }

  onCancel = () => {
    const { params } = this.props
    const { studyID, studyVersionID } = params
    browserHistory.push(`/studies/${studyID}/${studyVersionID}/communication`)
  }

  render() {
    const {
      currentStudy,
      deploySending,
      deploySent,
      isCreate,
      readOnly,
      saveAndDeploy,
      saveAnnouncement,
      saveSending,
      saveSent,
      schedule,
      sendDisabled,
      studyLock,
      translations,
      advancedOptions,
      showAdvancedOption,
      hasStudyPromotion,
    } = this.props

    const { isSMS } = this.state
    const communicationType = isSMS ? 'SMS' : 'Announcement'
    const sendStateClassName = sendDisabled ? 'send-state' : '' // for cursor wait during sendState

    // This variable allows more translations to be added and saved to an announcement.
    let disabledEdit = readOnly
    if (!isSMS && translations) disabledEdit = !translations

    const sendButtonContent = {
      content: 'Send',
      sendingContent: 'Sending',
      sentContent: '...Sent!',
    }
    const saveButtonContent = {
      content: 'Save',
      sendingContent: 'Saving',
      sentContent: '...Saved!',
    }

    const pageTitle = isCreate ? `New` : readOnly ? `View` : `Edit`

    setPageTitle(`${pageTitle} ${communicationType} | Study ${currentStudy.id} | ${currentStudy.name}`)

    return (
      <div className={`announcement page ${sendStateClassName}`}>
        <h4 className='navbar-header'>
          {isCreate ? `New ${communicationType}` : readOnly ? `View ${communicationType}` : `Edit ${communicationType}`}
        </h4>
        {readOnly && !isSMS && <ReadStatus {...this.props} />}
        {!!schedule && (
          <RecipientSection {...this.props} isSMS={isSMS} disabled={readOnly || sendDisabled || studyLock} />
        )}
        <MessageSection {...this.props} isSMS={isSMS} disabled={readOnly || sendDisabled || studyLock} />
        <h4>Delivery Details</h4>
        {!!schedule && (
          <DeliverySection {...this.props} isSMS={isSMS} disabled={readOnly || sendDisabled || studyLock} />
        )}
        {!disabledEdit && (
          <div className='button-list'>
            <Button
              onClick={() => !studyLock && this.onCancel()}
              grey
              id='cancel'
              content='Cancel'
              disabled={sendDisabled || studyLock}
            />
            {!disabledEdit && !hasStudyPromotion && (
              <Button
                id='save'
                onClick={() =>
                  !studyLock && saveAnnouncement(null, !isSMS, showAdvancedOption ? advancedOptions : null)
                }
                grey={!disabledEdit && !readOnly}
                content={buttonContentOnSendState(saveButtonContent, saveSending, saveSent)}
                loading={saveSending}
                disabled={sendDisabled || studyLock}
              />
            )}
            {!readOnly && (
              <Button
                id='send'
                className='deploy-btn'
                onClick={() => !studyLock && saveAndDeploy(!isSMS, showAdvancedOption ? advancedOptions : null)}
                content={buttonContentOnSendState(sendButtonContent, deploySending, deploySent)}
                loading={deploySending}
                disabled={sendDisabled || studyLock}
              />
            )}
          </div>
        )}
      </div>
    )
  }
}

AnnouncementPage.propTypes = {
  advancedOptions: PropTypes.objectOf(PropTypes.object),
  baseSiteID: PropTypes.number,
  currentStudy: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  }),
  deploySending: PropTypes.bool,
  deploySent: PropTypes.bool,
  studyID: PropTypes.number,
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  params: PropTypes.shape({
    siteID: PropTypes.string,
    studyID: PropTypes.string,
    studyVersionID: PropTypes.string,
    smsID: PropTypes.string,
    announcementID: PropTypes.string,
  }),
  isCreate: PropTypes.bool,
  readOnly: PropTypes.bool,
  saveAnnouncement: PropTypes.func,
  saveAndDeploy: PropTypes.func,
  saveSending: PropTypes.bool,
  saveSent: PropTypes.bool,
  sendDisabled: PropTypes.bool,
  setCommunicationType: PropTypes.func,
  setNavBarProps: PropTypes.func,
  showAdvancedOption: PropTypes.bool,
  studyLock: PropTypes.bool,
  is_sms: PropTypes.bool,
  resetAnnouncement: PropTypes.func,
  translations: PropTypes.objectOf(PropTypes.object),
}

export default AdvancedOptionsHOC(AnnouncementPage)
